import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Coins, 
  Plus, 
  Search, 
  Shield, 
  Eye, 
  MoreVertical,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Archive,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export function AssetsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [assets, setAssets] = useState([
    {
      id: 'asset_1',
      name: 'Propiedad Comercial Centro',
      type: 'Real Estate',
      value: '2,500,000',
      tokens: '2500',
      status: 'certified',
      certifications: {
        nardiha: { status: 'approved', date: '2024-01-15', auditor: 'Juan Pérez' },
        infra: { status: 'approved', date: '2024-01-16', auditor: 'María García' }
      },
      holders: 156,
      lastUpdate: '2024-12-05',
      roi: '+12.5%'
    },
    {
      id: 'asset_2',
      name: 'Fondo de Inversión Tech',
      type: 'Fund',
      value: '5,000,000',
      tokens: '5000',
      status: 'certified',
      certifications: {
        nardiha: { status: 'approved', date: '2024-01-10', auditor: 'Carlos Ruiz' },
        infra: { status: 'approved', date: '2024-01-11', auditor: 'Ana López' }
      },
      holders: 342,
      lastUpdate: '2024-12-04',
      roi: '+18.3%'
    },
    {
      id: 'asset_3',
      name: 'Vehículos Corporativos',
      type: 'Fleet',
      value: '450,000',
      tokens: '450',
      status: 'pending_infra',
      certifications: {
        nardiha: { status: 'approved', date: '2024-12-01', auditor: 'Juan Pérez' },
        infra: { status: 'pending', date: null, auditor: null }
      },
      holders: 23,
      lastUpdate: '2024-12-01',
      roi: 'N/A'
    },
    {
      id: 'asset_4',
      name: 'Patente Tecnológica AI-2024',
      type: 'IP',
      value: '1,200,000',
      tokens: '1200',
      status: 'pending_nardiha',
      certifications: {
        nardiha: { status: 'pending', date: null, auditor: null },
        infra: { status: 'pending', date: null, auditor: null }
      },
      holders: 0,
      lastUpdate: '2024-11-28',
      roi: 'N/A'
    },
  ]);
  const [auditingId, setAuditingId] = useState<string | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [assetToArchive, setAssetToArchive] = useState<string | null>(null);

  const filteredAssets = assets.filter(a => 
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'certified':
        return <Badge className="bg-green-500 text-xs"><CheckCircle2 className="mr-1 h-3 w-3" />Certificado</Badge>;
      case 'pending_infra':
        return <Badge variant="secondary" className="text-xs"><Clock className="mr-1 h-3 w-3" />Pendiente Infra</Badge>;
      case 'pending_nardiha':
        return <Badge variant="secondary" className="text-xs"><Clock className="mr-1 h-3 w-3" />Pendiente Nardiha</Badge>;
      default:
        return <Badge variant="outline" className="text-xs"><AlertCircle className="mr-1 h-3 w-3" />En Revisión</Badge>;
    }
  };

  const stats = [
    { label: 'Total Activos', value: assets.length.toString(), icon: Coins },
    { label: 'Valor Total', value: '$12.5M', icon: TrendingUp },
    { label: 'Certificados', value: assets.filter(a => a.status === 'certified').length.toString(), icon: CheckCircle2 },
    { label: 'Pendientes', value: assets.filter(a => a.status.includes('pending')).length.toString(), icon: Clock },
  ];

  const handleViewDetails = (assetId: string, assetName: string) => {
    toast.info(`Abriendo detalles de "${assetName}"...`);
  };

  const handleAudit = async (assetId: string, assetName: string) => {
    setAuditingId(assetId);
    toast.info(`Iniciando auditoría de "${assetName}"...`);
    
    // Simular proceso de auditoría
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    setAssets(prev => prev.map(a => 
      a.id === assetId 
        ? {
            ...a,
            status: 'certified',
            certifications: {
              nardiha: { status: 'approved', date: '2024-12-07', auditor: 'Sistema Automático' },
              infra: { status: 'approved', date: '2024-12-07', auditor: 'Sistema Automático' }
            }
          }
        : a
    ));
    
    setAuditingId(null);
    toast.success(`Activo "${assetName}" certificado exitosamente`);
  };

  const handleExportReport = (assetId: string, assetName: string) => {
    toast.success(`Exportando reporte de "${assetName}"...`);
    // Simular descarga de reporte
    setTimeout(() => {
      toast.success('Reporte descargado correctamente');
    }, 1500);
  };

  const handleArchiveAsset = () => {
    if (assetToArchive) {
      const asset = assets.find(a => a.id === assetToArchive);
      setAssets(prev => prev.filter(a => a.id !== assetToArchive));
      toast.success(`Activo "${asset?.name}" archivado correctamente`);
      setArchiveDialogOpen(false);
      setAssetToArchive(null);
    }
  };

  const openArchiveDialog = (assetId: string) => {
    setAssetToArchive(assetId);
    setArchiveDialogOpen(true);
  };

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">Gestión de Activos</h1>
          <p className="text-muted-foreground">
            Tokenización y auditoría de activos corporativos
          </p>
        </div>
        <Button className="bg-primary hover:bg-primary/90" asChild>
          <Link to="/dashboard/assets/new">
            <Plus className="mr-2 h-4 w-4" />
            Tokenizar Activo
          </Link>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="relative overflow-hidden border-border">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.label}</CardTitle>
                <Icon className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar activos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-11"
          />
        </div>
      </div>

      {/* Assets List */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">Todos ({assets.length})</TabsTrigger>
          <TabsTrigger value="certified">
            Certificados ({assets.filter(a => a.status === 'certified').length})
          </TabsTrigger>
          <TabsTrigger value="pending">
            Pendientes ({assets.filter(a => a.status.includes('pending')).length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4 mt-6">
          {filteredAssets.map((asset) => (
            <Card key={asset.id} className="relative overflow-hidden border-border hover:shadow-lg transition-shadow">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500">
                      <Coins className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <CardTitle className="mb-1.5">{asset.name}</CardTitle>
                      <CardDescription>ID: {asset.id}</CardDescription>
                      <div className="flex items-center gap-2 mt-3">
                        {getStatusBadge(asset.status)}
                        <Badge variant="outline" className="text-xs">{asset.type}</Badge>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewDetails(asset.id, asset.name)}>
                        <Eye className="mr-2 h-4 w-4" />
                        Ver detalles
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAudit(asset.id, asset.name)}>
                        <Shield className="mr-2 h-4 w-4" />
                        Auditar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportReport(asset.id, asset.name)}>
                        <FileText className="mr-2 h-4 w-4" />
                        Exportar reporte
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-destructive"
                        onClick={() => openArchiveDialog(asset.id)}
                      >
                        <Archive className="mr-2 h-4 w-4" />
                        Archivar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 font-medium">Valor</p>
                    <p className="text-lg font-semibold">${asset.value}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 font-medium">Tokens</p>
                    <p className="text-lg font-semibold">{asset.tokens}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 font-medium">Holders</p>
                    <p className="text-lg font-semibold">{asset.holders}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 font-medium">ROI</p>
                    <p className={`text-lg font-semibold ${asset.roi !== 'N/A' && 'text-green-600 dark:text-green-500'}`}>{asset.roi}</p>
                  </div>
                </div>

                {/* Certification Status */}
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Certificación Nardiha</span>
                    </div>
                    {asset.certifications.nardiha.status === 'approved' ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-muted-foreground">
                          {asset.certifications.nardiha.date} por {asset.certifications.nardiha.auditor}
                        </span>
                      </div>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Pendiente</Badge>
                    )}
                  </div>
                  <Progress 
                    value={asset.certifications.nardiha.status === 'approved' ? 100 : 0} 
                    className="h-1.5"
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-purple-500" />
                      <span className="text-sm font-medium">Certificación Infra</span>
                    </div>
                    {asset.certifications.infra.status === 'approved' ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-muted-foreground">
                          {asset.certifications.infra.date} por {asset.certifications.infra.auditor}
                        </span>
                      </div>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Pendiente</Badge>
                    )}
                  </div>
                  <Progress 
                    value={asset.certifications.infra.status === 'approved' ? 100 : 0} 
                    className="h-1.5"
                  />
                </div>

                <div className="flex gap-2 mt-4">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleViewDetails(asset.id, asset.name)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Ver detalles
                  </Button>
                  {asset.status !== 'certified' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleAudit(asset.id, asset.name)}
                      disabled={auditingId === asset.id}
                    >
                      {auditingId === asset.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Auditando...
                        </>
                      ) : (
                        <>
                          <Shield className="mr-2 h-4 w-4" />
                          Auditar
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="certified" className="space-y-4 mt-6">
          {filteredAssets.filter(a => a.status === 'certified').map((asset) => (
            <Card key={asset.id} className="relative overflow-hidden border-border hover:shadow-lg transition-shadow">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
              {/* Same content as above */}
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="pending" className="space-y-4 mt-6">
          {filteredAssets.filter(a => a.status.includes('pending')).map((asset) => (
            <Card key={asset.id} className="relative overflow-hidden border-border hover:shadow-lg transition-shadow">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
              {/* Same content as above */}
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Archive Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archivar Activo</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres archivar este activo? 
              Podrás restaurarlo más tarde desde el archivo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={handleArchiveAsset}
            >
              Archivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
